import { Navigate, useLocation } from "react-router-dom";

import { isAuthenticated } from "../../lib/api/tokenStore";
import { AUTH_ROUTES } from "./authFlow";
import { readSignupSession } from "./authSession";

/**
 * Guards for the auth flow.
 *
 * Every step depends on the last, so an unguarded URL is a crash waiting to
 * happen — and once signed in, `/auth*` must bounce into the app or the back
 * button drops a logged-in user onto the login form.
 *
 * `readSignupSession` expires the session itself, so an out-of-date one reads
 * as absent here rather than as a stale `sessionId` that 400s later.
 */
const AuthStep = ({ step, children }) => {
  const location = useLocation();
  const session = readSignupSession();
  const authed = isAuthenticated();

  switch (step) {
    // Entry points. Signed-in users are sent into the app instead.
    case "start":
    case "reactivate":
      return authed ? <Navigate to={AUTH_ROUTES.home} replace /> : children;

    case "verify":
      if (authed) return <Navigate to={AUTH_ROUTES.home} replace />;
      if (!session?.sessionId && !session?.traceId) {
        return <Navigate to={AUTH_ROUTES.getStarted} replace />;
      }
      return children;

    case "details":
      if (authed) return <Navigate to={AUTH_ROUTES.home} replace />;
      if (!session?.sessionId) {
        return <Navigate to={AUTH_ROUTES.getStarted} replace />;
      }
      return children;

    // Authenticated, but only reachable mid-signup. Without a draft there is
    // nothing left to save, so the user belongs in the app.
    case "photo":
      if (!authed) return <Navigate to={AUTH_ROUTES.getStarted} replace />;
      if (!session?.profileDraft) return <Navigate to={AUTH_ROUTES.home} replace />;
      return children;

    // Everything behind the login wall, preserving where they were headed.
    case "authenticated":
      return authed ? (
        children
      ) : (
        <Navigate
          to={AUTH_ROUTES.getStarted}
          replace
          state={{ from: location.pathname + location.search }}
        />
      );

    default:
      return children;
  }
};

export default AuthStep;
